import React from 'react';
import { useMutation } from '@apollo/client';
import * as Sentry from '@sentry/react';
import { AlertDialogProps } from '@radix-ui/react-alert-dialog';

import { ToasterVariant, useToaster } from 'hooks/useToaster';
import {
  Modal,
  ModalTitle,
  ModalHeader,
  ModalFooter,
  ModalContent
} from '@epoch-ui/components/Modal/modal';
import { Button } from '@epoch-ui/components/Button';
import {
  ADMIN_REMOVE_PLUS_ONE_GUEST_MUTATION,
  AdminRemovePlusOneGuestVariables,
  AdminRemovePlusOneGuestResponse
} from 'graphql/mutations/rsvp_control/plus_one/admin-remove-plus-one-guest';

import { useGuestSideSheetContext } from '../../guestSideSheetContext';

interface RemovePlusOneConfirmDialogProps extends AlertDialogProps {
  handleRemovePlusOne: () => void;
}

export const RemovePlusOneConfirmDialog: React.FC<RemovePlusOneConfirmDialogProps> = ({
  open,
  onOpenChange,
  handleRemovePlusOne
}) => {
  const {
    guestData: { guest: ownerGuest },
    selectedPlusOneGuest,
    eventOrSessionId: eventId
  } = useGuestSideSheetContext();
  const plusOneGuest = selectedPlusOneGuest!;
  const { showToast } = useToaster();
  const [removePlusOneGuestMutation, { loading }] = useMutation<
    AdminRemovePlusOneGuestResponse,
    AdminRemovePlusOneGuestVariables
  >(ADMIN_REMOVE_PLUS_ONE_GUEST_MUTATION, {
    onCompleted: () => {
      showToast(`${plusOneGuest.firstName} has been removed.`);
      handleRemovePlusOne();
    },
    onError: (error) => {
      showToast('Something went wrong', { variant: ToasterVariant.ERROR });
      Sentry.setContext('RSVP Control Remove Plus-One Guest', {
        ownerEmail: ownerGuest.email,
        plusOneId: plusOneGuest.plusOneId,
        event: eventId
      });
      Sentry.captureException(error);
    }
  });

  return (
    <Modal open={open} onOpenChange={onOpenChange}>
      <ModalContent size="sm" className="tw-gap-5 tw-text-black" showCloseButton={false}>
        <ModalHeader className="tw-mb-0">
          <ModalTitle>Are you sure?</ModalTitle>
        </ModalHeader>
        <p className="tw-m-0 tw-leading-5 tw-text-dark-black">
          {ownerGuest.firstName} will be notified through email and Slack their plus one has been
          removed from the Epoch event. You can&apos;t undo this action.
        </p>
        <ModalFooter className="tw-mt-0">
          <div className="tw-flex tw-items-end tw-justify-end tw-gap-3">
            <Button
              variant="secondary"
              size="sm"
              className="tw-rounded-xl"
              onClick={(): void => {
                if (onOpenChange) onOpenChange(false);
              }}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              variant="destructive"
              className="tw-rounded-xl"
              onClick={() =>
                removePlusOneGuestMutation({
                  variables: {
                    input: {
                      gid: ownerGuest.gid,
                      eventId,
                      plusOneId: plusOneGuest.plusOneId
                    }
                  }
                })
              }
              loading={loading}
              disabled={loading}
            >
              Remove guest
            </Button>
          </div>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
